import { getRealizedPnl } from '@/features/account/utils/math';
import { useCurrentAccount } from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  COLLATERAL_SYMBOL,
  useMarketStore,
} from '@/store/use-markets-store';
import { OrderType } from '@/types/enums';
import {
  BigDecimal,
  bigIntToDecimalStr,
  bigIntToNum,
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
  parseInputToDecimalStr,
  validatedDecimalStr,
} from '@/utils/value-format';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useStore } from 'zustand';

import { TpslEditFormSchema } from '@/features/account/components/tpsl-edit';
import { cn } from '@/utils/cn';
import { abbrFromSym } from '@/utils/token-symbol';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { validateFinalLimitPrice, validateLimitPrice } from '../utils/helpers';
import { getPriceFromPnl, getSizeFromNumeraire } from '../utils/math';
import { Input } from './input';
import { UnitSelect } from './unit-select';

/**
 * Component handles single Take Profit OR Stop Loss order.
 */

interface TpslInputProps {
  symbol: string;
  showOrderType?: boolean;
  index: number;
  orderType: OrderType.STOP_LOSS | OrderType.TAKE_PROFIT;
  shouldReset?: boolean;
}

const pnlUnits = ['%', COLLATERAL_SYMBOL] as const;

type PnlUnit = (typeof pnlUnits)[number];

export default function EditTpslInput({
  orderType,
  symbol,
  showOrderType = false,
  shouldReset = false,
  index,
}: TpslInputProps) {
  const { t } = useTranslation();
  const form = useFormContext<TpslEditFormSchema>();

  const name = orderType === OrderType.TAKE_PROFIT ? 'tps' : 'sls';
  const fields = form.watch(name);

  const shouldSizeAutoResize = useMemo(() => {
    return fields.some((field) => !field.isSubmitted);
  }, [fields]);

  const { midPrice, priceDecimals, sizeDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      midPrice: state.marketData.midPrice,
      priceDecimals: state.marketSpec.priceDecimals,
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );

  const { minLimitPrice, maxLimitPrice } = useMemo(() => {
    const minLimitPrice = midPrice / 10n; // TODO: round up
    const maxLimitPrice = midPrice * 10n; // TODO: round down
    return { minLimitPrice, maxLimitPrice };
  }, [midPrice]);

  const currentPosition = useCurrentAccount((state) => {
    const position = state.positions[symbol];
    if (!position) return;
    return {
      isLong: position.isLong,
      size: position.size,
      cost: position.cost,
      entryFunding: position.entryFunding,
      initMarginRatio: position.initMarginRatio,
      initMargin: position.initMargin.bigint,
    };
  });

  const { field: limitPriceField } = useController({
    name: `${name}.${index}.limitPrice`,
    control: form.control,
  });
  const { field: sizeField } = useController({
    name: `${name}.${index}.size`,
    control: form.control,
  });

  const limitPrice = parseDecimalToBigInt(
    form.watch(`${name}.${index}.limitPrice`),
    priceDecimals,
  );
  const size = parseDecimalToBigInt(
    form.watch(`${name}.${index}.size`),
    sizeDecimals,
  );

  const handleChangePriceInput = (value: string) => {
    value = validatedDecimalStr(value, Number(priceDecimals), 10);
    value = validateLimitPrice(symbol, value);
    const valueBigInt = parseDecimalToBigInt(value, priceDecimals);
    if (valueBigInt > maxLimitPrice) {
      return;
    }
    limitPriceField.onChange(value);
    setLastChanged('price');
  };

  const [pnlUnit, setPnlUnit] = useState<PnlUnit>(pnlUnits[0]);
  const [pnl, setPnl] = useState<BigDecimal>();
  const [roePct, setRoePct] = useState<BigDecimal>();

  const handleChangePnlUnit = (value: string) => {
    setPnlUnit(value as PnlUnit);
    setLastChanged('pnl');
  };

  useEffect(() => {
    if (!shouldReset) return;
    setLastChanged('price');
    limitPriceField.onChange('');
  }, [shouldReset]);

  const defaultPnlInput = useMemo(() => {
    if (
      limitPriceField.value === '' ||
      !currentPosition ||
      currentPosition.size === 0n
    ) {
      return '';
    }

    const { marketSpec } = useMarketStore(symbol).getState();
    // temp same code as below
    const sign = orderType === OrderType.TAKE_PROFIT ? 1n : -1n;
    const expectedPnl = getRealizedPnl(
      symbol,
      {
        isBuy: !currentPosition.isLong,
        lastFilledSize: parseDecimalToBigInt(
          sizeField.value,
          marketSpec.sizeDecimals,
        ),
        lastFilledPrice: parseDecimalToBigInt(
          limitPriceField.value,
          marketSpec.priceDecimals,
        ),
        settledFunding: 0n, // Assume no funding
        fees: 0n, // Assume no fees
      },
      currentPosition,
    );
    const initMargin = currentPosition.initMargin;
    const expectedRoe =
      initMargin !== 0n ? (expectedPnl.bigint * 10n ** 6n) / initMargin : 0n;
    return formatBigInt(sign * expectedRoe, 4n, { digits: 0, round: 'ceil' });
  }, [symbol, currentPosition]);

  const [pnlInput, setPnlInput] = useState(defaultPnlInput);

  const handleChangePnlInput = (value: string) => {
    const inputValue = value.replace(/,/g, '');
    if (inputValue !== '' && !/^(\d+|\d*\.\d{0,2})$/.test(inputValue)) {
      return;
    }
    setPnlInput((prev) =>
      inputValue.endsWith('.') && prev.length > inputValue.length
        ? inputValue.slice(0, -1)
        : inputValue,
    );
    setLastChanged('pnl');
  };

  const handleBlurPnlInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value.endsWith('.')) return;
    setPnlInput(e.target.value.slice(0, -1));
  };

  const [lastChanged, setLastChanged] = useState<'price' | 'pnl' | null>(null);

  const sizeUnits = [...pnlUnits, abbrFromSym(symbol)];
  const [sizeUnit, setSizeUnit] = useState(sizeUnits[0]);

  const updatedSizeInput = useMemo(() => {
    if (!currentPosition?.size || !fields) return '';
    const size = bigIntToNum(currentPosition.size, sizeDecimals);
    const sizePct = Math.floor((size / fields.length / size) * 100);
    const remainder =
      index === fields.length - 1 ? 100 - sizePct * fields.length : 0;
    return (sizePct + remainder).toString();
  }, [index, fields?.length, currentPosition?.size, sizeDecimals]);

  const defaultSizeInput = useMemo(() => {
    if (!currentPosition?.size) return '';
    const sizePct = (size * 10n ** 6n) / currentPosition.size;
    return formatBigInt(sizePct, 4n, { digits: 0, round: 'ceil' });
  }, [sizeDecimals, currentPosition?.size, size]);

  const [sizeInput, setSizeInput] = useState(
    shouldSizeAutoResize ? updatedSizeInput : defaultSizeInput,
  );
  // const [limitPriceInput, setLimitPriceInput] = useState(limitPriceWatch);

  useEffect(() => {
    if (sizeUnit !== '%' || !shouldSizeAutoResize) return;
    setLastChanged('price');
    setSizeInput(updatedSizeInput);
  }, [updatedSizeInput, shouldSizeAutoResize]);

  const handleChangeSizeUnit = (newUnit: string) => {
    if (sizeUnit === newUnit) return;
    const input = sizeInput || '0';

    switch (sizeUnit) {
      case '%':
        const percentage = parseDecimalToBigInt(
          parseInputToDecimalStr(input, 0),
          0,
        );
        if (newUnit === COLLATERAL_SYMBOL) {
          // % -> USD
          const usdcValue =
            ((currentPosition?.size ?? 0n) * midPrice * percentage) / 100n;
          setSizeInput(
            formatBigInt(usdcValue, priceDecimals + sizeDecimals, {
              maxDigits: 2,
              round: 'floor',
              commas: false,
            }),
          );
        } else {
          // % -> coin
          const coinValue = ((currentPosition?.size ?? 0n) * percentage) / 100n;
          setSizeInput(bigIntToDecimalStr(coinValue, sizeDecimals));
        }
        break;
      case COLLATERAL_SYMBOL:
        const usdcTotal = (currentPosition?.size ?? 0n) * midPrice;
        const usdcValue = parseDecimalToBigInt(
          input,
          priceDecimals + sizeDecimals,
        );
        const percentageValue = (usdcValue * 100n + usdcTotal - 1n) / usdcTotal;
        if (newUnit === '%') {
          // USD -> %
          setSizeInput(
            bigIntToDecimalStr(
              percentageValue > 100n ? 100n : percentageValue,
              0,
            ),
          );
        } else {
          // USD -> coin
          const coinTotal = currentPosition?.size ?? 0n;
          const coinValue = (coinTotal * percentageValue) / 100n;
          setSizeInput(bigIntToDecimalStr(coinValue, sizeDecimals));
        }
        break;
      case abbrFromSym(symbol):
        const coinsTotal = currentPosition?.size ?? 0n;
        const coinsValue = parseDecimalToBigInt(input, sizeDecimals);
        const coinsPercentage =
          (coinsValue * 100n + coinsTotal - 1n) / coinsTotal;
        if (newUnit === '%') {
          // coin -> %
          setSizeInput(bigIntToDecimalStr(coinsPercentage, 0));
        } else {
          // coin -> USDC
          const usdcTotal = (currentPosition?.size ?? 0n) * midPrice;
          const usdcValue = (usdcTotal * coinsPercentage) / 100n;
          setSizeInput(
            formatBigInt(usdcValue, priceDecimals + sizeDecimals, {
              maxDigits: 2,
              commas: false,
            }),
          );
        }
        break;
    }
    setSizeUnit(newUnit);
  };

  const handleChangeSizeInput = (value: string) => {
    switch (sizeUnit) {
      case COLLATERAL_SYMBOL:
        value = validatedDecimalStr(value, 2, 10);
        if (
          parseDecimalToBigInt(value, sizeDecimals + priceDecimals) >
          (currentPosition?.size ?? 0n) * midPrice
        ) {
          return;
        }
        setSizeInput(value);
        break;

      case '%':
        value = validatedDecimalStr(value, 0, 3);
        if (Number(value) > 100) {
          return;
        }
        setSizeInput(value);
        break;

      case abbrFromSym(symbol):
        value = validatedDecimalStr(value, Number(sizeDecimals), 10);
        if (
          parseDecimalToBigInt(value, sizeDecimals) >
          (currentPosition?.size ?? 0n)
        )
          return;
        setSizeInput(value);
        break;

      default:
        throw new Error('Invalid size unit');
    }
  };

  // set form.size from sizeInput and sizeUnit
  useEffect(() => {
    if (!currentPosition) return;

    let size: bigint;

    const { marketSpec } = useMarketStore(symbol).getState();

    switch (sizeUnit) {
      case COLLATERAL_SYMBOL:
        const numeraireSize = parseDecimalToBigInt(
          sizeInput,
          COLLATERAL_DECIMALS,
        );
        size = getSizeFromNumeraire(symbol, numeraireSize, midPrice);
        break;

      case '%':
        const sizePct = parseDecimalToBigInt(sizeInput, 4n);
        size = (sizePct * currentPosition.size) / 10n ** 6n;
        break;

      case abbrFromSym(symbol):
        size = parseDecimalToBigInt(sizeInput, marketSpec.sizeDecimals);
        break;

      default:
        throw new Error('Invalid size unit');
    }
    sizeField.onChange(bigIntToDecimalStr(size, marketSpec.sizeDecimals));
  }, [symbol, sizeInput, sizeUnit, currentPosition?.size]);

  // useEffect(() => {
  //   setLastChanged('price');
  // }, [limitPriceWatch]);

  // Set pnl from price
  useEffect(() => {
    if (lastChanged && lastChanged !== 'price') return;

    // if we want price to update when we change fields, just add marketData to dependency array and setLastChanged to null at end

    if (limitPrice === 0n || !currentPosition) {
      setPnlInput('');
      return;
    }

    const { marketSpec } = useMarketStore(symbol).getState();

    const sign = orderType === OrderType.TAKE_PROFIT ? 1n : -1n;
    const orderPrice = parseDecimalToBigInt(
      limitPriceField.value,
      marketSpec.priceDecimals,
    );

    const expectedPnl =
      sign *
      getRealizedPnl(
        symbol,
        {
          isBuy: !currentPosition.isLong,
          lastFilledSize: size,
          lastFilledPrice: orderPrice,
          settledFunding: 0n, // Assume no funding
          fees: 0n, // Assume no fees
        },
        currentPosition,
      ).bigint;
    const expectedPnlDecimal = formatBigInt(expectedPnl, COLLATERAL_DECIMALS, {
      digits: 2, // quantize to 2 decimal places?
      round: 'ceil',
    });
    const pctChange =
      currentPosition.initMargin !== 0n
        ? (expectedPnl * 10n ** 4n) / currentPosition.initMargin
        : 0n;
    const pctChangeDecimal = formatBigInt(pctChange, 2n, {
      digits: 0,
      round: 'ceil',
      commas: false,
    });

    setPnl({ bigint: sign * expectedPnl, decimal: expectedPnlDecimal });
    setRoePct({
      bigint: sign * pctChange,
      decimal: pctChangeDecimal,
    });

    switch (pnlUnit) {
      case COLLATERAL_SYMBOL:
        setPnlInput(expectedPnlDecimal);
        break;
      case '%':
        setPnlInput(pctChangeDecimal);
        break;
    }
  }, [currentPosition, symbol, lastChanged, limitPrice, size]);

  // Set price from pnl
  useEffect(() => {
    if (lastChanged !== 'pnl') return;

    if (pnlInput === '' || !currentPosition) {
      limitPriceField.onChange('');
      return;
    }

    if (pnlInput) {
      const sign = orderType === OrderType.TAKE_PROFIT ? 1n : -1n;

      let pnl: bigint;
      let orderPrice: BigDecimal;

      switch (pnlUnit) {
        case COLLATERAL_SYMBOL: // must have size defined
          if (size === 0n) {
            limitPriceField.onChange('');

            setPnl(undefined);
            setRoePct(undefined);

            return;
          }

          pnl = sign * parseDecimalToBigInt(pnlInput, COLLATERAL_DECIMALS);
          orderPrice = getPriceFromPnl(symbol, pnl, size, currentPosition);
          const pctChange =
            currentPosition.initMargin !== 0n
              ? (pnl * 10n ** 4n) / currentPosition.initMargin
              : 0n;

          limitPriceField.onChange(
            validateFinalLimitPrice(symbol, orderPrice.decimal),
          );

          setPnl({
            bigint: pnl,
            decimal: formatNumber(pnlInput, { maxDigits: 2 }),
          });
          setRoePct({
            bigint: pctChange,
            decimal: formatBigInt(pctChange, 2n, {
              digits: 0,
              round: 'ceil',
              commas: false,
            }),
          });

          break;

        case '%':
          const pnlPct = sign * parseDecimalToBigInt(pnlInput, 2n);
          pnl = (pnlPct * currentPosition.initMargin) / 10n ** 4n; // note we are multiplying by pct so divide by 10^2 to multiply by fraction
          orderPrice = getPriceFromPnl(symbol, pnl, size, currentPosition);

          limitPriceField.onChange(
            validateFinalLimitPrice(symbol, orderPrice.decimal),
          );

          setPnl({
            bigint: pnl,
            decimal: formatBigInt(pnl, COLLATERAL_DECIMALS, {
              digits: 2,
              round: 'ceil',
            }),
          });
          setRoePct({
            bigint: pnlPct,
            decimal: formatNumber(pnlInput, {
              digits: 0,
              round: 'ceil',
              commas: false,
            }),
          });

          break;
      }
    }
  }, [pnlInput, pnlUnit, currentPosition, lastChanged, size]);

  if (!currentPosition) return;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2.5">
            <span className="text-sm text-vestgrey-500">
              {showOrderType &&
                (orderType === OrderType.TAKE_PROFIT
                  ? t('tpslInput.tp')
                  : t('tpslInput.sl'))}{' '}
              {t('tpslInput.price')}
            </span>
            <div className="flex flex-1">
              <Input
                className="flex-1"
                {...limitPriceField}
                onChange={handleChangePriceInput}
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2.5">
            <span className="w-8 text-right text-sm text-vestgrey-500">
              {orderType === OrderType.TAKE_PROFIT
                ? t('tpslInput.profit')
                : t('tpslInput.loss')}
            </span>
            <div className="flex flex-1">
              <Input
                className="flex-1"
                value={pnlInput}
                onChange={handleChangePnlInput}
                onBlur={handleBlurPnlInput}
              />
              <UnitSelect
                units={pnlUnits}
                onSelect={handleChangePnlUnit}
                disabled={
                  parseDecimalToBigInt(sizeField.value, sizeDecimals) === 0n
                }
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col sm:flex-row sm:items-center">
            <span className="mb-1 text-sm text-vestgrey-500 sm:mb-0 sm:mr-2.5">
              {t('tpslInput.size')}
            </span>
            <div className="flex flex-1">
              <Input
                className="flex-1"
                value={sizeInput}
                onChange={handleChangeSizeInput}
              />
              <UnitSelect
                units={[...pnlUnits, abbrFromSym(symbol)]}
                onSelect={handleChangeSizeUnit}
              />
            </div>
          </div>
        </div>
        {size > 0n && limitPrice > 0n && pnlInput && pnl && roePct && (
          <div className="text-right text-sm">
            <span className="text-vestgrey-100">{t('tpslInput.estPnl')} </span>

            <span
              className={cn(
                'h-12 text-foreground opacity-100',
                pnl.bigint < 0n && 'text-red',
                pnl.bigint > 0n && 'text-green',
              )}
            >
              {pnl.bigint >= 0n ? '+' : '-'}
              {pnl.decimal.replace('-', '')} / {roePct.decimal.replace('-', '')}
              %
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
